import React, { useCallback, useMemo, useState } from 'react';
import {
  Button, ButtonColor, SpacingName, Modal, ModalSize, TypographyName,
} from '@doveit/lego';
import { NextPage } from 'next';
import AppLayout from '../client/layouts/app-layout/AppLayout';
import PageLayout, { PageLayoutSize } from '../client/layouts/page-layout/PageLayout';
import PageHead, { PageHeadProps } from '../client/components/page-head/PageHead';
import { requireFromStaticAsset } from '../client/utils/assetsUtils';
import { buildServicesURLObject } from '../client/utils/links';
import PageSection from '../client/components/page-section/PageSection';
import { CTA, TextAlign } from '../client/types';
import CreateLead from '../client/containers/create-lead/CreateLead';
import { OurServicesSectionProps } from '../client/templates/common/our-services-section/OurServicesSection';
import { MiniInfoBlockProps } from '../client/components/mini-info-block/MiniInfoBlock';
import variables from '../client/themes/variables';
import { GUIDES_BY_IDS } from '../client/graphql/queries/guide';
import { Article } from '../domain/graphcms';
import CardsHList from '../client/components/cards-hlist/CardsHList';
import ArticleVerticalCard from '../client/components/article-vertical-card/ArticleVerticalCard';
import { mapArticles } from '../client/graphql/mappers/articleMappers';
import { ArticleType } from '../domain/articleType';
import { getBreadcrumbStructuredData, getStructuredData } from '../data/structured';
import ServicesHList from '../client/components/services-hlist/ServicesHList';
import LogoList, { LogoListProps } from '../client/components/logo-list/LogoList';
import { getServices } from '../services/our-services/ourServices';
import Intro from '../client/components/intro/Intro';
import MiniInfoBlockList from '../client/components/mini-info-block-list/MiniInfoBlockList';
import CreateLeadSection from '../client/templates/common/create-lead-section/CreateLeadSection';
import { sendLeadSubmitEventAndRedirect } from '../client/utils/tracking';
import StepsToSuccess, { StepsToSuccessProps } from '../client/components/steps-to-success/StepsToSuccess';
import { BreadcrumbItem } from '../client/components/breadcrumb/Breadcrumb';
import { withError, withGetServerSideError } from '../client/hocs/with-server-side-error/withServerSideError';
import { importDynamicData } from '../services/importDynamicData';
import initGraphQLClient from '../client/graphql/initGraphQLClient';

export interface ServicePromotionProps {
  SEO: PageHeadProps['SEO'],
  intro: {
    title: string,
    description: string,
    cta: CTA,
    image: {
      src: string,
      alt: string,
    },
  },
  howItWorks: {
    title: string,
    description: string,
    steps: StepsToSuccessProps['steps'],
    successStep: StepsToSuccessProps['successStep'],
  },
  advantages: {
    title: string,
    description: string,
    items: MiniInfoBlockProps[],
  },
  portals: {
    title: string,
    description: string,
    items: LogoListProps['items'],
  },
  form: {
    title: string,
    intro: string,
    label: string,
  },
  ourServices: OurServicesSectionProps,
  relatedGuides: {
    title: string,
    guides: Article[],
  },
}

export const ServicePromotion: NextPage<ServicePromotionProps> = ({
  SEO,
  intro,
  howItWorks,
  advantages,
  portals,
  form,
  ourServices,
  relatedGuides,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentCTA, setCurrentCTA] = useState<string>();
  const guides = mapArticles(relatedGuides.guides, ArticleType.GUIDE);

  const openModal = useCallback((dataRef: string) => () => {
    setCurrentCTA(dataRef);
    setIsOpen(true);
  }, []);

  const onSuccess = useCallback(sendLeadSubmitEventAndRedirect, []);

  const breadcrumb = useMemo<BreadcrumbItem[]>(() => [{
    label: 'I nostri servizi',
    urlObject: buildServicesURLObject(),
  },
  {
    label: 'Fotografia e Virtual tour',
  }], []);

  return (
    <AppLayout>
      <PageHead
        SEO={SEO}
        structuredData={getStructuredData(
          getBreadcrumbStructuredData(breadcrumb),
        )}
      />
      <PageLayout>
        <PageLayout.Wrapper maxWidth={PageLayoutSize.L}>
          <PageLayout.Breadcrumb items={breadcrumb} />
          <PageLayout.Header>
            <Intro
              title={intro.title}
              description={intro.description}
              image={intro.image}
            >
              <Button
                color={ButtonColor.SECONDARY}
                label={intro.cta.label}
                dataRef={intro.cta.dataRef}
                dataTracking={intro.cta.dataTracking}
                onClick={openModal(intro.cta.dataTracking)}
              />
            </Intro>
          </PageLayout.Header>
        </PageLayout.Wrapper>

        <PageLayout.Content>
          <PageLayout.Main>
            <PageSection padding={[SpacingName.BIGGEST, 0, SpacingName.BIGGER]}>
              <PageSection.Wrapper>
                <PageSection.Title>
                  {howItWorks.title}
                </PageSection.Title>

                <PageSection.Description
                  text={howItWorks.description}
                  size={TypographyName.H4}
                  color="neutrals.medium"
                />

                <PageSection.Block>
                  <StepsToSuccess
                    steps={howItWorks.steps.map((step) => ({
                      ...step,
                      cta: {
                        ...step.cta,
                        onClick: openModal(step.cta.dataTracking),
                      },
                    }))}
                    successStep={{
                      ...howItWorks.successStep,
                      cta: {
                        ...howItWorks.successStep.cta,
                        onClick: openModal(howItWorks.successStep.cta.dataTracking),
                      },
                      image: {
                        src: requireFromStaticAsset(howItWorks.successStep.image.src),
                        alt: howItWorks.successStep.image.alt,
                      },
                    }}
                  />
                </PageSection.Block>
              </PageSection.Wrapper>
            </PageSection>

            <PageSection
              backgroundGradient={variables.sectionBackgroundGradient}
              padding={[SpacingName.BIG, 0, SpacingName.BIGGER]}
            >
              <PageSection.Wrapper>
                <PageSection.Title
                  content={advantages.title}
                />
                <PageSection.Description
                  text={advantages.description}
                  size={TypographyName.H4}
                  color="neutrals.medium"
                />
                <PageSection.Block>
                  <MiniInfoBlockList items={advantages.items} />
                </PageSection.Block>
              </PageSection.Wrapper>
            </PageSection>

            <PageSection
              backgroundColor="neutrals.lighter"
              padding={[SpacingName.BIG, 0, SpacingName.BIGGER]}
            >
              <PageSection.Wrapper>
                <PageSection.Title>
                  {portals.title}
                </PageSection.Title>
                <PageSection.Description
                  text={portals.description}
                  size={TypographyName.H4}
                  color="neutrals.medium"
                />
                <PageSection.Block>
                  <LogoList
                    items={portals.items.map((item) => ({
                      ...item,
                      picture: requireFromStaticAsset(item.picture),
                    }))}
                    inlineItems={portals.items.length}
                  />
                </PageSection.Block>
              </PageSection.Wrapper>
            </PageSection>

            <PageSection>
              <PageSection.Wrapper>
                <PageSection.Title
                  content={form.title}
                  textAlign={TextAlign.CENTER}
                />
                <PageSection.Description
                  text={form.intro}
                  textAlign={TextAlign.CENTER}
                  size={TypographyName.H4}
                  color="neutrals.medium"
                />
                <CreateLeadSection
                  title={form.label}
                  dataRef="lead-form-main"
                  dataTracking="lead-form-main-submit"
                  onSuccess={(lead) => onSuccess('main', lead)}
                />
              </PageSection.Wrapper>
            </PageSection>

            <PageSection>
              <PageSection.Wrapper>
                <PageSection.Title
                  size={TypographyName.H3}
                >
                  {ourServices.title}
                </PageSection.Title>
                <PageSection.Description text={ourServices.description} />
              </PageSection.Wrapper>
              <PageSection.Wrapper withoutPad>
                <PageSection.Block>
                  <ServicesHList
                    services={ourServices.services}
                  />
                </PageSection.Block>
              </PageSection.Wrapper>
            </PageSection>

            <PageSection backgroundColor="neutrals.lighter">
              <PageSection.Wrapper>
                <PageSection.Title
                  size={TypographyName.H3}
                >
                  {relatedGuides.title}
                </PageSection.Title>
              </PageSection.Wrapper>
              <PageSection.Wrapper withoutPad>
                <PageSection.Block>
                  <CardsHList sideSpacing={SpacingName.SMALL}>
                    {guides.map((guide) => (
                      <CardsHList.Item key={guide.id}>
                        <ArticleVerticalCard {...guide} />
                      </CardsHList.Item>
                    ))}
                  </CardsHList>
                </PageSection.Block>
              </PageSection.Wrapper>
            </PageSection>
          </PageLayout.Main>
        </PageLayout.Content>
      </PageLayout>

      <Modal
        open={isOpen}
        size={ModalSize.MEDIUM}
        onCloseHandler={() => setIsOpen(false)}
        dataRef="lead-modal"
      >
        <Modal.Close />
        <Modal.Header title="Compila il form, ti ricontatteremo entro 48h" />
        <Modal.Body padded>
          <CreateLead
            dataRef="lead-form-modal"
            dataTracking="lead-form-modal-submit"
            onSuccess={(lead) => onSuccess(`modal:${currentCTA}`, lead)}
          />
        </Modal.Body>
      </Modal>
    </AppLayout>
  );
};

export const getServerSideProps = withGetServerSideError<ServicePromotionProps>(async () => {
  const graphQLClient = initGraphQLClient();

  const [common, pageData, services] = await Promise.all([
    importDynamicData('common.yml'),
    importDynamicData('pages/service-promotion.yml'),
    getServices(),
  ]);

  const { data: { guides } } = await graphQLClient.query({
    query: GUIDES_BY_IDS,
    variables: {
      ids: pageData.relatedGuides.ids,
    },
  });

  return {
    props: {
      ...common,
      ...pageData,
      relatedGuides: {
        ...pageData.relatedGuides,
        guides,
      },
      ourServices: {
        ...pageData.ourServices,
        services,
      },
    },
  };
});

export default withError(ServicePromotion);
